import { Injectable } from '@angular/core';
import { CardapioClientePerfilDTO } from '../../models/cardapio-digital/cardapio-digital.model';

/**
 * Servico de gerenciamento de perfis de cliente no localStorage.
 * Salva/carrega perfis de identificacao (nome, DDI, telefone, endereco)
 * para preenchimento automatico no cardapio digital.
 *
 * Segue o mesmo padrao do CardapioHistoricoService (try/catch, graceful degradation).
 * Diferente do Historico, NAO usa BehaviorSubject pois os dados sao lidos sob demanda.
 */
@Injectable({ providedIn: 'root' })
export class CardapioClienteCacheService {
  private readonly CACHE_PREFIX = 'cardapio_cliente_';
  private readonly MAX_PERFIS = 10;

  /**
   * Retorna todos os perfis salvos para o slug.
   * Retorna [] se localStorage indisponivel ou dados corrompidos.
   */
  getPerfis(slug: string): CardapioClientePerfilDTO[] {
    return this.lerDoCache(slug);
  }

  /**
   * Salva ou atualiza um perfil.
   * Deduplicacao: se ja existe perfil com mesmo nome (case-insensitive) + mesmo telefone,
   * atualiza o perfil existente ao inves de criar novo.
   * Se atingir MAX_PERFIS sem match, remove o mais antigo (FIFO).
   */
  salvarPerfil(slug: string, perfil: CardapioClientePerfilDTO): void {
    const perfis = this.lerDoCache(slug);

    const indexExistente = perfis.findIndex(p =>
      p.nome.trim().toLowerCase() === perfil.nome.trim().toLowerCase()
      && p.telefone === perfil.telefone
    );

    if (indexExistente >= 0) {
      // Atualiza perfil existente (endereco, DDI, etc.)
      perfis[indexExistente] = perfil;
    } else {
      // Adiciona no inicio (mais recente primeiro)
      perfis.unshift(perfil);
      // Aplica limite FIFO
      if (perfis.length > this.MAX_PERFIS) {
        perfis.splice(this.MAX_PERFIS);
      }
    }

    this.sincronizar(slug, perfis);
  }

  /**
   * Remove um perfil por indice.
   * (Reservado para uso futuro - nao sera chamado nesta feature)
   */
  removerPerfil(slug: string, index: number): void {
    const perfis = this.lerDoCache(slug);
    if (index >= 0 && index < perfis.length) {
      perfis.splice(index, 1);
      this.sincronizar(slug, perfis);
    }
  }

  // --- Persistencia localStorage ---

  private sincronizar(slug: string, perfis: CardapioClientePerfilDTO[]): void {
    try {
      localStorage.setItem(this.CACHE_PREFIX + slug, JSON.stringify(perfis));
    } catch {
      // localStorage indisponivel ou cheio
    }
  }

  private lerDoCache(slug: string): CardapioClientePerfilDTO[] {
    try {
      const raw = localStorage.getItem(this.CACHE_PREFIX + slug);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as CardapioClientePerfilDTO[];
    } catch {
      return [];
    }
  }
}
